import { basicColors, IColor } from '@Common/Controls/BasicColors';

import { ColorRangeType } from '@Common/Enums/Enum';
import documentHelper from '@Common/Helpers/DocumentHelper';
import { ThemeHelper } from '@Common/Helpers/ThemeHelper';
import { BaseStyle, ColorStyle } from '@InstanceFactory';

export default class ColorHelper {
    private static customColorNamePrefix = 'customColor_';
    private static badNamedColor = 'rgba(0, 0, 0, 1)';
    private static customColors: _.Dictionary<string>;

    public static getColorFromHexString(hex): { a: number; r: number; g: number; b: number } {
        hex = hex.replace('#', '');
        let opacity = 255;
        let index = 0;
        if (hex.length === 8) {
            opacity = parseInt(hex.substring(0, 2), 16);
            index = 2;
        }
        const r = parseInt(hex.substring(index, index + 2), 16);
        const g = parseInt(hex.substring(index + 2, index + 4), 16);
        const b = parseInt(hex.substring(index + 4, index + 6), 16);
        return { a: opacity / 255, r: r, g: g, b: b };
    }

    public static getStringColor(colorObject): string {
        if (colorObject.a != null) {
            return `rgba(${colorObject.r}, ${colorObject.g}, ${colorObject.b}, ${colorObject.a})`;
        } else {
            return `rgb(${colorObject.r}, ${colorObject.g}, ${colorObject.b})`;
        }
    }

    public static getStringColorHex(colorObject, withOpacity?: boolean): string {
        let a = 0;
        if (colorObject.a !== undefined) {
            a = colorObject.a;
        }

        function formatToHex(number: number): string {
            let str = number.toString(16);
            if (str.length === 1) {
                str = `0${str}`;
            }
            return str;
        }

        return (
            '#' +
            (withOpacity !== false ? formatToHex(Math.round(255 * a)) : '') +
            formatToHex(colorObject.r) +
            formatToHex(colorObject.g) +
            formatToHex(colorObject.b)
        );
    }

    public static getColorFromAnyString(str): any {
        if (str) {
            if (str[0] === '#') {
                return this.getColorFromHexString(str);
            }
            return this.getColorFromString(str);
        }
        return undefined;
    }

    public static generateSomePastelColor(tintColor): any {
        if (typeof tintColor == 'string') {
            tintColor = ColorHelper.getColorFromAnyString(tintColor);
        } else if (tintColor == undefined) {
            tintColor = { r: 255, g: 255, b: 255 };
        }

        if (isFinite(tintColor.r) && isFinite(tintColor.g) && isFinite(tintColor.b)) {
            const randomR = Math.floor(Math.random() * 256);
            const randomG = Math.floor(Math.random() * 256);
            const randomB = Math.floor(Math.random() * 256);
            const r = Math.floor((randomR + tintColor.r) / 2);
            const g = Math.floor((randomG + tintColor.g) / 2);
            const b = Math.floor((randomB + tintColor.b) / 2);
            return `rgb(${r},${g},${b})`;
        }
        return null;
    }

    public static hue2rgb(p, q, t): any {
        if (t < 0) {
            t += 1;
        }
        if (t > 1) {
            t -= 1;
        }
        if (t < 1 / 6) {
            return p + (q - p) * 6 * t;
        }
        if (t < 1 / 2) {
            return q;
        }
        if (t < 2 / 3) {
            return p + (q - p) * (2 / 3 - t) * 6;
        }
        return p;
    }

    public static hslToRgb(hsl): { r: number; g: number; b: number } {
        const h = hsl.h;
        const s = hsl.s;
        const l = hsl.l;
        let r: number;
        let g: number;
        let b: number;
        if (s === 0) {
            r = g = b = l; // achromatic
        } else {
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = this.hue2rgb(p, q, h + 1 / 3);
            g = this.hue2rgb(p, q, h);
            b = this.hue2rgb(p, q, h - 1 / 3);
        }
        return {
            r: Math.round(r * 255),
            g: Math.round(g * 255),
            b: Math.round(b * 255)
        };
    }

    public static rgbToHsl(color): { h: number; s: number; l: number } {
        const r = color.r / 255;
        const g = color.g / 255;
        const b = color.b / 255;
        const max = Math.max(r, g, b),
            min = Math.min(r, g, b);
        let h: number;
        let s: number;
        const l = (max + min) / 2;
        if (max === min) {
            h = s = 0; // achromatic
        } else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0);
                    break;
                case g:
                    h = (b - r) / d + 2;
                    break;
                case b:
                    h = (r - g) / d + 4;
                    break;
            }
            h /= 6;
        }
        return { h: h, s: s, l: l };
    }

    public static rgbColorEquals(color1: IColor, color2: IColor): boolean {
        const a1 = typeof color1.a === 'undefined' ? 1 : color1.a;
        const a2 = typeof color2.a === 'undefined' ? 1 : color2.a;
        return color1.r === color2.r && color1.g === color2.g && color1.b === color2.b && a1 === a2;
    }

    public static colorEquals(color1: string | IColor, color2: string | IColor): boolean {
        let rgb1 = color1 as IColor,
            rgb2 = color2 as IColor;
        if (typeof color1 === 'string') {
            rgb1 = ColorHelper.getColorFromString(color1);
        }
        if (typeof color2 === 'string') {
            rgb2 = ColorHelper.getColorFromString(color2);
        }

        return this.rgbColorEquals(rgb1, rgb2);
    }

    public static brighten(color: any, alpha: number): string {
        const rgb = this.getColorFromAnyString(color);
        if (rgb) {
            rgb.r = Math.max(Math.min(rgb.r + alpha * 255, 255), 0);
            rgb.g = Math.max(Math.min(rgb.g + alpha * 255, 255), 0);
            rgb.b = Math.max(Math.min(rgb.b + alpha * 255, 255), 0);
            return this.getStringColor(rgb);
        }
        return undefined;
    }

    public static adjustColor(color, value, max, min, minAlpha, colorRangeType?, palette?): string {
        const minHue = 0.1;
        const minSaturation = 0.15;
        const minLightness = 0.25;
        const maxLightness = 0.75;
        if (min > max || value > max || value < min) {
            return color;
        }
        if (!minAlpha) {
            minAlpha = 0;
        }
        const k = max === min ? 1 : (value - min) / (max - min);
        if (palette && palette.length > 0) {
            return palette[Math.floor(palette.length * k * 0.999)];
        }
        if (colorRangeType === ColorRangeType.hue) {
            const hls = this.rgbToHsl(color);
            hls.h = k * (1 - minHue) + minHue;
            const newRGB = this.hslToRgb(hls);
            return this.getStringColor({
                a: color.a,
                r: newRGB.r,
                g: newRGB.g,
                b: newRGB.b
            });
        }
        if (colorRangeType === ColorRangeType.saturation) {
            const hls = this.rgbToHsl(color);
            hls.s = k * (1 - minSaturation) + minSaturation;
            const newRgb = this.hslToRgb(hls);
            return this.getStringColor({
                a: color.a,
                r: newRgb.r,
                g: newRgb.g,
                b: newRgb.b
            });
        }
        if (colorRangeType === ColorRangeType.lightness) {
            const hls = this.rgbToHsl(color);
            hls.l = 1 - (k * maxLightness * (1 - minLightness) + minLightness);
            const newRGB = this.hslToRgb(hls);
            return this.getStringColor({
                a: color.a,
                r: newRGB.r,
                g: newRGB.g,
                b: newRGB.b
            });
        }
        return this.getStringColor({
            a: k < minAlpha ? minAlpha : k,
            r: color.r,
            g: color.g,
            b: color.b
        });
    }

    public static getNamedColor(title, projectId): string {
        const colors = documentHelper.getProject(projectId).colorsList.colors;
        const titleColor = _(colors).find(color => color.title === title);
        return titleColor && titleColor.color ? titleColor.color : null;
    }

    public static getColorFromString(color: string, nullIfFailed = false): IColor {
        const result = /rgba?\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*(?:,\s*([0-9]?(?:\.[0-9]+)?)\s*)?\)/.exec(color);
        if (result) {
            return {
                a: result[4] == null ? 1 : parseFloat(result[4]),
                r: parseInt(result[1], 10),
                g: parseInt(result[2], 10),
                b: parseInt(result[3], 10)
            };
        }
        if (nullIfFailed) {
            return null;
        }
        return { a: 0, r: 0, g: 0, b: 0 };
    }

    public static hexToRgba(hexStr: string): string {
        const color = this.getColorFromHexString(hexStr);
        if (color.a == null) {
            color.a = 1;
        }
        return this.getStringColor(color);
    }

    public static rgbaToHex(rgbaStr: string): string {
        const color = this.getColorFromString(rgbaStr);
        return this.getStringColorHex(color);
    }

    public static getCustomColorName(name: string): string {
        return ColorHelper.customColorNamePrefix + name;
    }

    public static getColorName(customColorName: string): string {
        if (ColorHelper.isCustomColor(customColorName)) {
            return customColorName.substring(ColorHelper.customColorNamePrefix.length);
        }
        return customColorName;
    }

    public static isCustomColor(color: string): boolean {
        return (color || '').startsWith(ColorHelper.customColorNamePrefix);
    }

    public static getCustomColors(): _.Dictionary<string> {
        ColorHelper.customColors = ColorHelper.customColors || ColorHelper.createCache(ThemeHelper.getStyles());

        return ColorHelper.customColors;
    }

    public static getRepresentationColor(color: IColor | string, namedColors = ColorHelper.getCustomColors()): string {
        if (color == null) {
            return null;
        }
        if (typeof color === 'object') {
            return this.getStringColor(color);
        }
        if (typeof color === 'string') {
            if (color.startsWith('#') || color.startsWith('rgb')) {
                return color;
            }
            if (ColorHelper.isCustomColor(color)) {
                return (namedColors && namedColors[ColorHelper.getColorName(color)]) || ColorHelper.badNamedColor;
            }
            const basicColor = basicColors.filter(c => c.name.toLowerCase() === color.toLowerCase()).map(c => c.rgba)[0];
            if (basicColor) {
                return this.getStringColor(basicColor);
            }
        }
        return color as string;
    }

    public static updateColorForElement(e: JQuery): void {
        e.find('[text-color]').each((i, element) => {
            const jElement = $(element);
            jElement.css('color', ColorHelper.getRepresentationColor(jElement.attr('text-color')));
        });
    }

    public static resetCache(styles: BaseStyle[] = null): void {
        if (styles === null) {
            ColorHelper.customColors = null;
        } else {
            ColorHelper.customColors = ColorHelper.createCache(styles);
        }
    }

    public static translateColor(style: BaseStyle, namedStyles: _.Dictionary<string> = ColorHelper.customColors): void {
        if (ColorStyle.isInstanceOfType(style)) {
            return;
        }

        ColorHelper.visitObject(style, (name, value) => {
            if (name === 'color' || (Number.isNumber(name) && typeof value === 'string')) {
                if (ColorHelper.isCustomColor(value)) {
                    const customColorName = ColorHelper.getColorName(value);
                    return namedStyles[customColorName] || ColorHelper.badNamedColor;
                }
            }
            return value;
        });
    }

    private static createCache(styles: BaseStyle[]): _.Dictionary<string> {
        return styles
            .filter(s => ColorStyle.isInstanceOfType(s))
            .searchTree(
                s => s.name,
                (s: ColorStyle) => s.color
            );
    }

    private static visitObject(obj: any, transformValue: (name: string | number, val: any) => any): void {
        _.forEach(obj, (value, key) => {
            const subObj = (obj[key] = transformValue(key, value));
            Object.isObject(subObj) && ColorHelper.visitObject(subObj, transformValue);
        });
    }
}
